import React from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeleteIcon from '@mui/icons-material/Delete';

import { PageHeader, DataTableCard, FilterChips, WizardDrawer, ConfirmDialog, FeatureUsageMeter } from '../ui';
import CouponWizardDrawer from './CouponWizardDrawer';
import buildCouponColumns from './buildCouponColumns';
import useCouponsPage from './useCouponsPage';

const Coupons = () => {
  const p = useCouponsPage();

  const columns = buildCouponColumns({
    t: p.t, isExpired: p.isExpired, openEditWizard: p.openEditWizard, openDeleteConfirm: p.openDeleteConfirm,
  });

  const filterOptions = [
    { value: 'all', label: p.t('All') },
    { value: 'active', label: p.t('Active') },
    { value: 'expired', label: p.t('Expired') },
  ];

  const steps = [
    { label: p.t('Basics') },
    { label: p.t('Discount') },
    { label: p.t('Products') },
    { label: p.t('Users') },
    { label: p.t('Expiry') },
  ];

  const stepInvalid = () => {
    if (p.activeStep === 0) return !p.form.code.trim();
    if (p.activeStep === 1) {
      const value = parseFloat(p.form.discountValue);
      if (!value || value <= 0) return true;
      if (p.form.discountType === 'PERCENT' && value > 100) return true;
    }
    return false;
  };

  return (
    <Box>
      <PageHeader
        title={p.t('Coupons')}
        subtitle={p.t('Create and manage discount coupons for your customers')}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search coupons...') }}
        actions={[
          {
            label: p.t('Create Coupon'), icon: AddIcon, variant: 'contained', onClick: p.openCreateWizard,
            disabled: p.planLimit?.atLimit, disabledReason: p.planLimit?.tooltip,
          },
        ]}
        helpKey="coupons"
        meter={<FeatureUsageMeter feature="coupons.active.max" label={p.t('Active coupons', { defaultValue: 'Active coupons' })} />}
      />

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        toolbar={(
          <FilterChips options={filterOptions} value={p.filterTab} onChange={p.setFilterTab} filtersLabel={p.t('Filters')} />
        )}
        empty={p.isNoMatch ? {
          kind: 'no-match',
          title: p.t('No coupons match "{{query}}"', { query: p.search, defaultValue: `No coupons match "${p.search}"` }),
          description: p.t('Try a different search or clear the filter.', { defaultValue: 'Try a different search or clear the filter.' }),
          actions: [{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); p.setFilterTab('all'); } }],
        } : {
          kind: 'none',
          icon: LocalOfferIcon,
          title: p.t('No coupons yet', { defaultValue: 'No coupons yet' }),
          description: p.t('Create a coupon to offer your customers a discount at checkout.', { defaultValue: 'Create a coupon to offer your customers a discount at checkout.' }),
          actions: [{ label: p.t('Create Coupon'), variant: 'contained', onClick: p.openCreateWizard }],
        }}
        error={{
          title: p.t("Couldn't load coupons", { defaultValue: "Couldn't load coupons" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: () => p.fetchCoupons(true),
        }}
        permission={{
          title: p.t("You don't have access to Coupons", { defaultValue: "You don't have access to Coupons" }),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <WizardDrawer
        open={p.wizardOpen}
        onClose={p.closeWizard}
        title={p.wizardMode === 'create' ? p.t('Create Coupon') : p.t('Edit Coupon')}
        steps={steps}
        activeStep={p.activeStep}
        onStepClick={p.setActiveStep}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        backLabel={p.t('Back')}
        nextLabel={p.t('Next')}
        finalLabel={p.saving
          ? (p.wizardMode === 'create' ? p.t('Creating...') : p.t('Updating...'))
          : (p.wizardMode === 'create' ? p.t('Create Coupon') : p.t('Save changes', { defaultValue: 'Save changes' }))}
        onBack={() => p.setActiveStep((s) => s - 1)}
        onNext={() => p.setActiveStep((s) => s + 1)}
        onFinish={p.handleSave}
        nextDisabled={p.saving || stepInvalid()}
        isDirty={p.isDirty}
        discardConfirm={{
          title: p.t('Discard changes?', { defaultValue: 'Discard changes?' }),
          description: p.t('Your coupon changes have not been saved.', { defaultValue: 'Your coupon changes have not been saved.' }),
          cancelLabel: p.t('Keep editing', { defaultValue: 'Keep editing' }),
          confirmLabel: p.t('Discard', { defaultValue: 'Discard' }),
        }}
      >
        <CouponWizardDrawer
          t={p.t}
          activeStep={p.activeStep}
          form={p.form}
          setForm={p.setForm}
          products={p.products}
          users={p.users}
          toggleProduct={p.toggleProduct}
          toggleUser={p.toggleUser}
        />
      </WizardDrawer>

      <ConfirmDialog
        open={p.confirmOpen}
        onClose={() => p.setConfirmOpen(false)}
        tone="danger"
        icon={DeleteIcon}
        title={p.t('Delete "{{code}}"?', { code: p.confirmTarget?.code, defaultValue: `Delete "${p.confirmTarget?.code}"?` })}
        description={p.t('This permanently deletes the coupon. This action cannot be undone.', { defaultValue: 'This permanently deletes the coupon. This action cannot be undone.' })}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Delete', { defaultValue: 'Delete' })}
        onConfirm={p.handleConfirmDelete}
        loading={p.deleting}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Coupons;
